import {
  ColorRangeMidPoint,
  ColorRangePoint,
  HexColor,
} from '@ardoq/api-types';
import {
  darken as polishedDarken,
  hsl,
  lighten,
  parseToHsl,
  parseToRgb,
  rgbToColorString,
  shade,
  tint,
  mix,
} from 'polished';
import { Alpha, HSLColorProps, HSVColorProps, RGB, RGBA } from './types';
import { isNumber } from 'lodash';
import { COLOR_NAME_TO_RGBA_MAP, RGBA_OBJ_WHITE } from './const';
import { DEFAULT_COLORS, getRangeColor, isStrictColorHex } from './cssColors';
import { createFifoCache } from '@ardoq/common-helpers';
import { RgbColor } from 'polished/lib/types/color';

const rgbRegex = /^ *rgb\( *([\d., ]*) *\) *$/i;
const rgbaRegex = /^ *rgba\( *([\d., ]*) *\) *$/i;

export const darken = polishedDarken;

// Originally from ardoqModelCSSManager
export const getDefaultCSSColor = (level: number) => {
  const cssColorsAsRecord: Record<string, string> = DEFAULT_COLORS;
  return cssColorsAsRecord[`level${level}`] || getRangeColor(level);
};

const isNumberInRange = (maybeNumber: any, min: number, max: number): boolean =>
  isNumber(maybeNumber) && maybeNumber >= min && maybeNumber <= max;

/** @param hexColor Hex color string, must start with # to be valid */
export const isValidHexColor = (hexColor: HexColor): boolean => {
  if (!hexColor.startsWith('#')) return false;
  try {
    const { red, green, blue } = parseToRgb(hexColor);
    return (
      isNumberInRange(red, 0, 255) &&
      isNumberInRange(green, 0, 255) &&
      isNumberInRange(blue, 0, 255)
    );
  } catch {
    return false;
  }
};

const isValidRgbString = (rgbString: string): boolean => {
  const regexResults = rgbRegex.exec(rgbString);
  if (!regexResults) return false;
  const [red, green, blue] = regexResults[1]
    .split(',')
    .map(str => str.trim())
    .map(parseFloat);
  return (
    isNumberInRange(red, 0, 255) &&
    isNumberInRange(green, 0, 255) &&
    isNumberInRange(blue, 0, 255)
  );
};

const isValidRgbaString = (rgbaString: string): boolean => {
  const regexResults = rgbaRegex.exec(rgbaString);
  if (!regexResults) return false;
  const [red, green, blue, alpha] = regexResults[1]
    .split(',')
    .map(str => str.trim())
    .map(parseFloat);
  return (
    isNumberInRange(red, 0, 255) &&
    isNumberInRange(green, 0, 255) &&
    isNumberInRange(blue, 0, 255) &&
    isNumberInRange(alpha, 0, 1)
  );
};

const CSS_COLOR_KEYWORD = /^[a-z][a-z-]*$/i;

/**
 * Whether a color can be interpolated into a stylesheet without escaping its
 * declaration. Hex and the functional forms are validated by format; a bare
 * identifier is inert whether or not it names a real color.
 */
export const isCssSafeColorValue = (value: string | undefined): boolean =>
  value !== undefined &&
  (isStrictColorHex(value) ||
    CSS_COLOR_KEYWORD.test(value) ||
    isValidRgbString(value) ||
    isValidRgbaString(value));

// HSV-HSL converters from https://en.wikipedia.org/wiki/HSL_and_HSV#Conversion_HSV_to_HSL
export const hslToHsv = ({
  hue,
  hslSaturation,
  lightness,
}: HSLColorProps): HSVColorProps => {
  const value = hslSaturation * Math.min(lightness, 1 - lightness) + lightness;
  const hsvSaturation = value ? 2 - (2 * lightness) / value : 0;
  return { hue, hsvSaturation, value };
};

export const hsvToHsl = ({
  hue,
  hsvSaturation,
  value,
}: HSVColorProps): HSLColorProps => {
  const lightness = value - (value * hsvSaturation) / 2;
  const m = Math.min(lightness, 1 - lightness);
  const hslSaturation = m ? (value - lightness) / m : 0;
  return { hue, hslSaturation, lightness };
};

/**
 * Convert from shortened Hex (e.g. #000) to full-length (#000000)
 * Assumes input is a valid 3-digit hex color.
 * */
const getFullHexFrom3Digit = (hexColor: HexColor): HexColor =>
  [
    '#',
    ...hexColor
      .slice(1)
      .split('')
      .map(hexChar => hexChar.repeat(2)),
  ].join('');

/**
 * Convert transparency value (0-1 inclusive) to Hex value to be used in 8-char hex colors
 * E.g.
 * */
const transparencyToHexValue = (alpha: Alpha): string => {
  const hexValue = Math.round(alpha * 255)
    .toString(16)
    .toUpperCase();
  return hexValue.length < 2 ? `0${hexValue}` : hexValue;
};

/** Given RGBA object, return a Hex color string that incorporates
 * transparency, if it is < 1.
 */
const getHexWithTransparency = ({
  red,
  green,
  blue,
  alpha,
}: RGBA): HexColor => {
  const hexColorNoTransparency = rgbToColorString({ red, green, blue });
  const hexColorNoTransparencyExpanded =
    hexColorNoTransparency.length === 4
      ? getFullHexFrom3Digit(hexColorNoTransparency)
      : hexColorNoTransparency;
  const hexTransparency = alpha < 1 ? transparencyToHexValue(alpha) : '';
  return `${hexColorNoTransparencyExpanded}${hexTransparency}`;
};

export const rgbaStringToObj = (rgbaStr: string): RGBA => {
  const regexResults = rgbaRegex.exec(rgbaStr);
  if (!(regexResults && isValidRgbaString(rgbaStr))) return RGBA_OBJ_WHITE;
  const [red, green, blue, alpha] = regexResults[1]
    .split(',')
    .map(str => str.trim())
    .map(parseFloat);
  return { red, green, blue, alpha };
};

const rgbStringToRgbaObj = (rgbStr: string): RGBA => {
  const regexResults = rgbRegex.exec(rgbStr);
  if (!(regexResults && isValidRgbString(rgbStr))) return RGBA_OBJ_WHITE;
  const [red, green, blue] = regexResults[1]
    .split(',')
    .map(str => str.trim())
    .map(parseFloat);
  return { red, green, blue, alpha: 1 };
};

export const hexColorToRgbaObj = (hexColor: HexColor): RGBA => {
  const { red, green, blue, alpha } = { alpha: 1, ...parseToRgb(hexColor) };
  return { red, green, blue, alpha };
};

export const rgbaObjToStr = ({ red, green, blue, alpha }: RGBA): string =>
  `rgba(${red},${green},${blue},${alpha})`;

/** Attempt to convert colorString of unknown type to RGBA object.
 * Otherwise fallback to RGBA white.
 * */
export const unknownColorTypeStrToRgbaObj = (colorString: string): RGBA => {
  if (isValidRgbaString(colorString)) return rgbaStringToObj(colorString);
  if (isValidRgbString(colorString)) return rgbStringToRgbaObj(colorString);
  if (isValidHexColor(colorString)) return hexColorToRgbaObj(colorString);
  if (COLOR_NAME_TO_RGBA_MAP.has(colorString))
    return COLOR_NAME_TO_RGBA_MAP.get(colorString)!;
  return RGBA_OBJ_WHITE;
};

/** Validate input color string. If valid hex or rgba, return color. Elses, return undefined. */
export const getValidHexOrRgbaColor = (
  colorString: string
): string | undefined => {
  const colorIsHex = isValidHexColor(colorString);
  const colorIsRgba = isValidRgbaString(colorString);
  if (colorIsHex || colorIsRgba) return colorString;
  return undefined;
};

/** For use in ardoqModelCSSManager when generating classnames based on colors */
export const getSanitizedRgbaClassName = (rgbaString: string): string =>
  rgbaString
    .toLowerCase()
    .replace('rgba(', 'filter')
    .replace(/\./g, '-')
    .replace(/,/g, '_')
    .replace(')', '');

/**
 * Converts a filter color string (hex or rgba) to a valid CSS class name.
 * Returns empty string for unsupported formats.
 */
export const sanitizeFilterColorClassName = (color: string): string => {
  if (color.startsWith('#')) return color.replace('#', 'filter');
  if (color.toLowerCase().startsWith('rgba'))
    return getSanitizedRgbaClassName(color);
  return '';
};

const COMPONENT_BACKGROUND_LIGHT = 0.8;

const shadedColor = shade(0.25);

export const getShadedColor = (color: string) => {
  try {
    return shadedColor(color);
  } catch {
    return '#ffffff';
  }
};

const getLightTintedColor = tint(COMPONENT_BACKGROUND_LIGHT);

export const getLightenedColor = createFifoCache<string, string>(500, color => {
  // support legacy behavior: if alpha < 1, just change the alpha.
  const rgba = rgbaStringToObj(color);
  if (rgba !== RGBA_OBJ_WHITE && 0 < rgba.alpha && rgba.alpha < 1) {
    return `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${(
      (1 - COMPONENT_BACKGROUND_LIGHT) *
      rgba.alpha
    ).toFixed(3)})`;
  }
  try {
    return getLightTintedColor(color);
  } catch {
    return '#ffffff';
  }
});

const TAG_FILL_TINT = 0.9;
const TAG_BORDER_TINT = 0.7;

const getTagFillTintedColor = tint(TAG_FILL_TINT);
const getTagBorderTintedColor = tint(TAG_BORDER_TINT);

export const getTagFillColor = createFifoCache<string, string>(500, color => {
  const rgba = rgbaStringToObj(color);
  if (rgba !== RGBA_OBJ_WHITE && 0 < rgba.alpha && rgba.alpha < 1) {
    return `rgba(${rgba.red}, ${rgba.green}, ${rgba.blue}, ${(
      (1 - TAG_FILL_TINT) *
      rgba.alpha
    ).toFixed(3)})`;
  }
  try {
    return getTagFillTintedColor(color);
  } catch {
    return '#ffffff';
  }
});

// Border colours are always fully opaque so no special alpha handling is needed here.
export const getTagBorderColor = createFifoCache<string, string>(
  500,
  (color: string): string => {
    try {
      return getTagBorderTintedColor(color);
    } catch {
      return '#ffffff';
    }
  }
);

export const normalizeColorToHex = (unknownColorValue: string) =>
  getHexWithTransparency(unknownColorTypeStrToRgbaObj(unknownColorValue));

/**
 * Similar as  to normalizeColorToHex, but if the color is invalid this FN returns null
 */
export const tryParseColorStringToHex = (
  unknownColorValue: string | undefined | null
) => {
  try {
    return unknownColorValue && parseToRgb(unknownColorValue)
      ? normalizeColorToHex(unknownColorValue)
      : null;
  } catch (error) {
    return null;
  }
};

type GetIsCssColorValueOptions = {
  ignoreTransparencies?: boolean;
};

export const getIsCssColorValue = (() => {
  let testSpan: HTMLSpanElement | null = null;
  let testContainer: HTMLDivElement | null = null;
  const cache = new Map<string, boolean>();
  const TEST_CONTAINER_ID = 'assets-and-test-container';
  const colorPrefixes = ['#', 'rgb', 'hsl'];

  return (
    str: string,
    { ignoreTransparencies }: GetIsCssColorValueOptions = {}
  ): boolean => {
    if (
      ignoreTransparencies &&
      str.startsWith('#') &&
      // #cccc - color with transparency
      // #cccccccc - color with transparency
      [5, 9].includes(str.length)
    ) {
      return false;
    }

    if (!colorPrefixes.some(prefix => str.startsWith(prefix))) {
      return false;
    }
    if (cache.has(str)) {
      return cache.get(str)!;
    }

    if (document && !testContainer) {
      testContainer = document.querySelector(`#${TEST_CONTAINER_ID}`);
      if (!testContainer) {
        testContainer = document.createElement('div');
        document.body.append(testContainer);
        testContainer.id = TEST_CONTAINER_ID;
        testContainer.style.cssText = `
          height: 0;
          left: -1000px;
          pointer-events: none;
          position: absolute;
          top: 0;
          width: 0;
        `;
      }
    }

    if (testContainer && !testSpan) {
      testSpan = document.createElement('span');
      testContainer.appendChild(testSpan);
    }

    if (testSpan) {
      testSpan.style.removeProperty('background-color');
      const backgroundColor = window
        .getComputedStyle(testSpan)
        .getPropertyValue('background-color');
      testSpan.style.backgroundColor = str;
      const isColorValue =
        window
          .getComputedStyle(testSpan)
          .getPropertyValue('background-color') !== backgroundColor;
      cache.set(str, isColorValue);
      return isColorValue;
    }
    return false;
  };
})();

export const hexToHsl = (hex: string): HSLColorProps => {
  const { hue, saturation, lightness } = parseToHsl(hex);
  return { hue, hslSaturation: saturation * 100, lightness: lightness * 100 };
};

export const hslToHex = ({
  hue,
  hslSaturation,
  lightness,
}: HSLColorProps): string => {
  const hex = hsl(hue, hslSaturation / 100, lightness / 100);
  /**
   * polished's hsl() returns 3-digit CSS shorthand when all three byte pairs
   * are identical digits (e.g. #8800FF → #80f). Expand to 6-digit so string
   * comparison works correctly.
   */
  return hex.length === 4 ? getFullHexFrom3Digit(hex) : hex;
};

export const rgbToHSL = ({ red, green, blue }: RGB): HSLColorProps => {
  const hsl = parseToHsl(`rgb(${red}, ${green}, ${blue})`);
  return {
    hue: hsl.hue,
    hslSaturation: hsl.saturation * 100,
    lightness: hsl.lightness * 100,
  };
};

const isValidRgbObject = (rgbObj: RgbColor): rgbObj is RgbColor =>
  Boolean(
    rgbObj &&
    rgbObj.red !== undefined &&
    rgbObj.green !== undefined &&
    rgbObj.blue !== undefined
  );
/**
 * Takes any color string that can be parsed to rgb object by polished,
 *
 * returns a string value for "values" prop of feColorMatrix, so that all
 * channels are modified towards the argument color.
 *
 * https://ardoqcom.atlassian.net/wiki/spaces/INSIGHT/pages/2532966456/Image+color+filters
 */
export const getFeColorMatrixValuesForColor = (color: string) => {
  const rgbObj = parseToRgb(color);
  if (isValidRgbObject(rgbObj)) {
    const { red, green, blue } = rgbObj;
    return getImageColorMatrixValuesForRgb(`${red}~~${green}~~${blue}`);
  }
};

const convertAlphaChannelValueToColor = (channelValue: number) =>
  channelValue ** 2 * 0.86;

const MINIMAL_GRAY_SCALE = [0.1, 0.1, 0.1];
const getImageColorMatrixValuesForRgb = createFifoCache(
  500,
  (rgbColorStringFifoCacheArgs: `${string}~~${string}~~${string}`) => {
    const rgb = rgbColorStringFifoCacheArgs
      .split('~~')
      .map(v => Number(v) / 255);

    const [red, green, blue] = rgb;

    const feColorMatrix = [
      [...MINIMAL_GRAY_SCALE, convertAlphaChannelValueToColor(red), 0.0], // r channel
      [...MINIMAL_GRAY_SCALE, convertAlphaChannelValueToColor(green), 0.0], // g channel
      [...MINIMAL_GRAY_SCALE, convertAlphaChannelValueToColor(blue), 0.0], // b channel
      [0, 0, 0, 1, 0], // alpha
    ];

    return feColorMatrix.map(channel => channel.join(',')).join(',');
  }
);

const calcLuminance = ({ red, green, blue }: RGB): number => {
  return (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
};

const isLightColor = (color: HexColor): boolean => {
  const rgb = parseToRgb(color);
  const luminance = calcLuminance(rgb);
  // A color is light if its luminance is greater than 0.5
  return luminance > 0.5;
};

/**
 * Adjusts the color by a given percentage based on whether the color is light or dark.
 * @param color - The color to be adjusted.
 * @param percentage - The percentage by which to adjust the color.
 * @returns The adjusted color.
 */
export const adaptiveColorAdjustment = (
  color: HexColor,
  percentage: number
): HexColor => {
  return isLightColor(color)
    ? darken(percentage, color)
    : lighten(percentage, color);
};

export const readableText = (color: HexColor): HexColor => {
  return isLightColor(color) ? '#000000' : '#ffffff';
};

type ColorRange = {
  min: ColorRangePoint;
  mid?: ColorRangeMidPoint;
  max: ColorRangePoint;
};

/**
 * Converts any valid CSS color to a hex string using canvas rendering.
 * Returns fallback for invalid or null colors.
 */

export function safeHexifyColor(
  color: string | null | undefined,
  fallback: string
): string {
  if (!color || !CSS.supports('color', color)) return fallback;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d', {
    willReadFrequently: true,
  });

  if (!ctx) return fallback;

  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;

  const toHex = (value: number): string => value.toString(16).padStart(2, '0');
  if (a === 255) {
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  // Composite semi-transparent color over white to get a stable hex
  const alpha = a / 255;
  const compositeR = Math.round(r * alpha + 255 * (1 - alpha));
  const compositeG = Math.round(g * alpha + 255 * (1 - alpha));
  const compositeB = Math.round(b * alpha + 255 * (1 - alpha));
  return `#${toHex(compositeR)}${toHex(compositeG)}${toHex(compositeB)}`;
}

/** Scales a value to a color within the provided color range */
export const scaleColor = (colorRange: ColorRange, value: number): HexColor => {
  const { min, max, mid: initialMid } = colorRange;

  if (!isNumber(min.value) || !isNumber(max.value)) {
    return '';
  }

  // If mid color point is provided without a value, set it to the midpoint between min and max
  const mid = initialMid
    ? {
        ...initialMid,
        value: isNumber(initialMid.value)
          ? initialMid.value
          : (min.value + max.value) / 2,
      }
    : undefined;

  if (mid && value <= mid.value) {
    const ratio =
      min.value === mid.value
        ? 0
        : (value - min.value) / (mid.value - min.value);
    return mix(Math.max(0, Math.min(1, ratio)), mid.color, min.color);
  }

  if (mid && value > mid.value) {
    const ratio =
      mid.value === max.value
        ? 1
        : (value - mid.value) / (max.value - mid.value);
    return mix(Math.max(0, Math.min(1, ratio)), max.color, mid.color);
  }

  const ratio =
    min.value === max.value ? 0 : (value - min.value) / (max.value - min.value);
  return mix(Math.max(0, Math.min(1, ratio)), max.color, min.color);
};
